import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  useLazyGetChronologyQuery,
  useLazyGetChronologyUndoRedoHistoryQuery,
  useLazyGetTimelineQuery,
  useLazyGetTimelineUndoRedoHistoryQuery,
} from '@/api';
import {
  selectSharedDataActiveTimelineChronologyType,
  setCaseIdData,
  TimelineChronologyType,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Toast } from '@repo/atomic-components';
import { TimelineChronologyTemplate } from '@/ui/templates';
import { List, Footer, TimelineDates, ActionsPanel } from './components';
import { useTimelineChronologyData } from './hooks';
import { BROADCAST_FORCE_TIMELINE_REFRESH, BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID } from '@/broadcast';
import { AppRoutesSearchParams } from '@/constants';
import { getIndexThatIncludesInsertZones } from './utils';
export const TimelineChronologyPage = () => {
  const { caseId } = useParams();

  const activeTimelineChronologyType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const dispatch = useAppDispatch();

  const [triggerGetTimeline, { isLoading: isGetTimelineLoading }] = useLazyGetTimelineQuery();
  const [triggerGetChronology, { isLoading: isGetChronologyLoading }] = useLazyGetChronologyQuery();
  const [triggerGetTimelineUndoRedoHistory, { isLoading: isGetTimelineUndoRedoHistoryLoading }] =
    useLazyGetTimelineUndoRedoHistoryQuery();
  const [triggerGetChronologyUndoRedoHistory, { isLoading: isGetChronologyUndoRedoHistoryLoading }] =
    useLazyGetChronologyUndoRedoHistoryQuery();

  const timelineChronologyData = useTimelineChronologyData();

  const refreshData = useCallback(() => {
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      triggerGetTimeline({ caseId });
    } else if (caseId && activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      triggerGetChronology({ caseId });
    }
  }, [caseId, activeTimelineChronologyType, triggerGetTimeline, triggerGetChronology]);

  useEffect(() => {
    const handleRefreshMessage = () => {
      refreshData();
    };

    window.addEventListener(BROADCAST_FORCE_TIMELINE_REFRESH, handleRefreshMessage);

    return () => {
      window.removeEventListener(BROADCAST_FORCE_TIMELINE_REFRESH, handleRefreshMessage);
    };
  }, [refreshData]);

  useEffect(() => {
    if (caseId) {
      dispatch(setCaseIdData(caseId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      triggerGetTimeline({ caseId });
      triggerGetTimelineUndoRedoHistory({ caseId });
    }

    if (caseId && activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      triggerGetChronology({ caseId });
      triggerGetChronologyUndoRedoHistory({ caseId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineChronologyType, caseId]);

  // Cross-tab "jump to chronology entry" support.
  //
  // Two entry points converge here:
  //   1) URL param `chronology_entry_id_on_init` — used when the bundle
  //      tab opened this page in a new tab because no chronology tab
  //      was live.
  //   2) BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID — used when a
  //      chronology tab was already open; the bundle tab pings it via
  //      BroadcastChannel and this listener handles the scroll.
  //
  // Kept as state (not a ref) so a broadcast that lands *after* the
  // entries have loaded still re-runs the scroll effect. Cleared once
  // the scroll has fired.
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentListEntries, listRef } = timelineChronologyData;
  const [pendingEntryId, setPendingEntryId] = useState<string | null>(() =>
    searchParams.get(AppRoutesSearchParams.chronologyEntryIdOnInit),
  );
  // Entry ID to briefly highlight after a cross-tab jump. Cleared after
  // the CSS animation has fully played out (see .chronology-entry-flash-*
  // in index.css — 6s duration) so React doesn't strip the class
  // mid-animation.
  //
  // `flashToken` alternates each time a flash is triggered so we can
  // restart the animation even when the same entry is clicked twice in
  // a row: setting the same id would be a no-op for React, and CSS
  // won't replay an animation on its own — but switching class variant
  // (`-a` <-> `-b`, driven by the token parity) causes the browser to
  // see a fresh animation-name and start from 0%.
  const [flashState, setFlashState] = useState<{ id: string; token: number } | null>(null);
  const flashTokenCounterRef = useRef(0);

  const triggerFlash = useCallback((entryId: string) => {
    flashTokenCounterRef.current += 1;
    setFlashState({ id: entryId, token: flashTokenCounterRef.current });
  }, []);

  useEffect(() => {
    if (!flashState) return;
    const t = window.setTimeout(() => setFlashState(null), 6200);
    return () => window.clearTimeout(t);
  }, [flashState]);

  useEffect(() => {
    const handleEntryJump = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const entryId: string | undefined = detail?.payload?.chronologyEntryId ?? detail?.chronologyEntryId;
      if (entryId) setPendingEntryId(entryId);
      // Reply so the sender knows the tab exists and doesn't open a fallback new tab.
      const reply = detail?.reply;
      if (typeof reply === 'function') reply({ received: true });
      try {
        window.focus();
      } catch {
        // ignore
      }
    };
    window.addEventListener(BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID, handleEntryJump);
    return () => {
      window.removeEventListener(BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID, handleEntryJump);
    };
  }, []);

  useEffect(() => {
    if (!pendingEntryId) return;
    if (activeTimelineChronologyType !== TimelineChronologyType.CHRONOLOGY) return;
    if (!currentListEntries?.length) return;
    // `currentListEntries` is entries-only (no date-header strings), which
    // is the same shape passed to InsertableListVirtuoso as `items`. That
    // matches `getIndexThatIncludesInsertZones` (which assumes an
    // entries-only index) — using the *mixed* list here would produce an
    // off-by-N index because Virtuoso doesn't know about the date
    // headers, they're rendered inside each item's own template.
    const indexToScroll = currentListEntries.findIndex((entry) => entry.id === pendingEntryId);
    if (indexToScroll === -1) return;
    // Snap-scroll (no `behavior: 'smooth'`) — smooth animation is unreliable
    // through a virtualised list because rows are mounted lazily as the
    // animation passes and variable heights can trigger re-anchoring
    // mid-scroll. Instead we snap, and a CSS flash on the target row
    // (see List.tsx, applies when `data-flash-entry-id` matches) draws
    // the user's eye to where we landed.
    listRef.current?.scrollToIndex(getIndexThatIncludesInsertZones(Math.max(indexToScroll - 1, 0)));
    triggerFlash(pendingEntryId);
    setPendingEntryId(null);
    // Strip the query param so a page refresh won't re-fire the scroll.
    if (searchParams.has(AppRoutesSearchParams.chronologyEntryIdOnInit)) {
      const next = new URLSearchParams(searchParams);
      next.delete(AppRoutesSearchParams.chronologyEntryIdOnInit);
      setSearchParams(next, { replace: true });
    }
  }, [
    pendingEntryId,
    activeTimelineChronologyType,
    currentListEntries,
    listRef,
    searchParams,
    setSearchParams,
    triggerFlash,
  ]);

  return (
    <>
      <TimelineChronologyTemplate
        isDataLoading={
          isGetTimelineLoading ||
          isGetTimelineUndoRedoHistoryLoading ||
          isGetChronologyLoading ||
          isGetChronologyUndoRedoHistoryLoading
        }
        actionsPanel={<ActionsPanel {...timelineChronologyData} />}
        contentLeftColumn={<TimelineDates ref={timelineChronologyData.timelineRef} {...timelineChronologyData} />}
        contentMainColumn={
          <List ref={timelineChronologyData.listRef} flashState={flashState} {...timelineChronologyData} />
        }
        footer={<Footer {...timelineChronologyData} />}
      />
      <Toast ref={timelineChronologyData.toastRef} />
    </>
  );
};
